/**
 * ProposalStatusReasonDialog
 *
 * Diálogo genérico de CONFIRMAÇÃO com MOTIVO obrigatório para transições de
 * situação destrutivas da proposta (recusar, cancelar, cancelar aceita).
 *
 * O motivo é publicado na RPC `update_proposal_status` (p_reason) e aparece na
 * timeline de histórico, garantindo rastreabilidade/auditoria da decisão.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Criação inicial (extraído de
 *            ProposalCancelAcceptedDialog para reuso em recusa/cancelamento).
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';

/** Limites do motivo (sanitização básica de input, revalidada no servidor) */
export const PROPOSAL_REASON_MAX_LENGTH = 500;
export const PROPOSAL_REASON_MIN_LENGTH = 5;

export interface ProposalStatusReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Executa a ação com o motivo informado */
  onConfirm: (reason: string) => void;
  /** Indica processamento em andamento */
  isPending?: boolean;
  /** Título do diálogo */
  title: string;
  /** Texto explicativo abaixo do título */
  description: string;
  /** Aviso destacado (InlineNotice) — opcional */
  noticeText?: string;
  /** Rótulo do campo de motivo */
  reasonLabel: string;
  /** Placeholder do campo de motivo */
  reasonPlaceholder?: string;
  /** Rótulo do botão de confirmação */
  confirmLabel: string;
}

export function ProposalStatusReasonDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  title,
  description,
  noticeText,
  reasonLabel,
  reasonPlaceholder = 'Descreva o motivo (registro de auditoria)',
  confirmLabel,
}: ProposalStatusReasonDialogProps) {
  const [reason, setReason] = useState('');

  // Motivo válido segundo a mesma regra do backend
  const isValid = reason.trim().length >= PROPOSAL_REASON_MIN_LENGTH;

  /** Fecha limpando o estado local */
  const handleOpenChange = (next: boolean) => {
    if (!next) setReason('');
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Empilhamento automático via DialogDepthContext (dialog.tsx v2.0.0) */}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {noticeText && <InlineNotice variant="warning">{noticeText}</InlineNotice>}

          <div className="space-y-1.5">
            <Label htmlFor="proposal-status-reason">
              {reasonLabel} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="proposal-status-reason"
              value={reason}
              maxLength={PROPOSAL_REASON_MAX_LENGTH}
              placeholder={reasonPlaceholder}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
            />
            <p className="text-[11px] text-muted-foreground">
              Mínimo de {PROPOSAL_REASON_MIN_LENGTH} caracteres · {reason.length}/
              {PROPOSAL_REASON_MAX_LENGTH}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            Voltar
          </Button>
          <Button
            variant="destructive"
            disabled={!isValid || isPending}
            onClick={() => onConfirm(reason.trim())}
          >
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
